import { useCallback, useState } from "react";
import { Mic, Loader2, ShieldAlert, ExternalLink } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Tooltip, TooltipContent, TooltipTrigger } from "@/components/ui/tooltip";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogDescription,
} from "@/components/ui/dialog";
import { useSpeechToText } from "@/hooks/useSpeechToText";
import {
  isVoiceInputSupported,
  checkMicrophonePermission,
  requestMicrophonePermission,
  describeMicrophoneAccessError,
  getMicrophoneSupportIssue,
  type MicrophoneAccessIssue,
  type MicrophoneHelpKind,
} from "@/lib/speechUtils";
import { useToast } from "@/hooks/use-toast";
import { cn } from "@/lib/utils";
import VoiceInputOverlay from "./VoiceInputOverlay";

interface VoiceInputButtonProps {
  onTranscript: (text: string) => void;
  variant?: "toolbar" | "inline";
  className?: string;
  disabled?: boolean;
  language?: string;
}

export default function VoiceInputButton({
  onTranscript,
  variant = "toolbar",
  className,
  disabled = false,
  language,
}: VoiceInputButtonProps) {
  const { toast } = useToast();
  const [helpKind, setHelpKind] = useState<MicrophoneHelpKind>(null);

  const presentIssue = useCallback(
    (issue: MicrophoneAccessIssue) => {
      if (issue.helpKind) {
        setHelpKind(issue.helpKind);
        return;
      }

      toast({
        title: "Voice Input",
        description: issue.message,
        variant: "destructive",
      });
    },
    [toast]
  );

  const handleTranscript = useCallback(
    (text: string) => {
      if (text.trim()) {
        onTranscript(text.trim());
      }
    },
    [onTranscript]
  );

  const handleError = useCallback(
    (error: string, issue?: MicrophoneAccessIssue) => {
      if (issue?.helpKind) {
        setHelpKind(issue.helpKind);
      }

      toast({
        title: "Voice Input",
        description: error,
        variant: "destructive",
      });
    },
    [toast]
  );

  const {
    isListening,
    isProcessing,
    interimTranscript,
    duration,
    audioLevel,
    startListening,
    stopListening,
    cancelListening,
    isSupported,
  } = useSpeechToText({
    language,
    onTranscript: handleTranscript,
    onError: handleError,
  });

  if (!isSupported || !isVoiceInputSupported()) {
    return null;
  }

  const handleClick = async () => {
    if (isListening) {
      stopListening();
      return;
    }

    if (isProcessing) return;

    const supportIssue = getMicrophoneSupportIssue();
    if (supportIssue) {
      presentIssue(supportIssue);
      return;
    }

    const permissionState = await checkMicrophonePermission();
    if (permissionState === "denied") {
      presentIssue(await describeMicrophoneAccessError("not-allowed"));
      return;
    }

    if (permissionState === "prompt") {
      try {
        const stream = await requestMicrophonePermission();
        stream.getTracks().forEach((track) => track.stop());
      } catch (error) {
        presentIssue(await describeMicrophoneAccessError(error));
        return;
      }
    }

    await startListening();
  };

  const isActive = isListening || isProcessing;

  const isChrome = /Chrome/.test(navigator.userAgent) && !/Edg/.test(navigator.userAgent);
  const isEdge = /Edg/.test(navigator.userAgent);
  const isFirefox = /Firefox/.test(navigator.userAgent);
  const isSafari = /Safari/.test(navigator.userAgent) && !/Chrome/.test(navigator.userAgent);

  const getBrowserSteps = () => {
    if (isChrome) {
      return [
        "Click the lock or tune icon in the address bar at the top",
        'Find "Microphone" in the dropdown and change it to "Allow"',
        "Refresh the page and try again",
      ];
    }

    if (isEdge) {
      return [
        "Click the lock icon in the address bar at the top",
        'Click "Permissions for this site"',
        'Set "Microphone" to "Allow"',
        "Refresh the page and try again",
      ];
    }

    if (isFirefox) {
      return [
        "Click the lock or shield icon in the address bar",
        'Click "Connection secure" and then "More information"',
        'Go to the "Permissions" tab and find "Use the Microphone"',
        'Uncheck "Use Default" and select "Allow"',
        "Refresh the page and try again",
      ];
    }

    if (isSafari) {
      return [
        "Open Safari, then go to Settings, Websites, and Microphone",
        'Find this website and change it to "Allow"',
        "Refresh the page and try again",
      ];
    }

    return [
      "Click the lock or site-settings icon in the address bar",
      'Find the microphone permission and change it to "Allow"',
      "Refresh the page and try again",
    ];
  };

  const getPolicySteps = () => [
    "Remove the response header `Permissions-Policy: microphone=()` from the reverse proxy, CDN, or server",
    "Remove the legacy header `Feature-Policy: microphone 'none'` if it is still being sent",
    "Redeploy the site, refresh the page, and try voice input again",
  ];

  const isPolicyHelp = helpKind === "policy";

  const helpDialog = (
    <Dialog open={helpKind !== null} onOpenChange={(open) => !open && setHelpKind(null)}>
      <DialogContent className="sm:max-w-md">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2">
            <ShieldAlert className="h-5 w-5 text-amber-500" />
            {isPolicyHelp ? "Microphone Blocked By Site Policy" : "Microphone Access Blocked"}
          </DialogTitle>
          <DialogDescription>
            {isPolicyHelp
              ? "This site is currently telling the browser to disable microphone access for everyone on this domain."
              : "Your browser has blocked microphone access for this site. Follow these steps to enable it:"}
          </DialogDescription>
        </DialogHeader>

        <div className="space-y-4 py-2">
          <ol className="space-y-3 text-sm">
            {(isPolicyHelp ? getPolicySteps() : getBrowserSteps()).map((step, index) => (
              <li key={index} className="flex gap-3">
                <span className="flex h-6 w-6 shrink-0 items-center justify-center rounded-full bg-primary text-primary-foreground text-xs font-medium">
                  {index + 1}
                </span>
                <span className="pt-0.5">{step}</span>
              </li>
            ))}
          </ol>

          <div className="rounded-lg bg-muted p-3 text-sm text-muted-foreground">
            <p className="mb-1 font-medium">Why is this happening?</p>
            <p>
              {isPolicyHelp
                ? "Server response headers override the browser site permission toggle. Users cannot fix this from the lock icon."
                : "Microphone access was previously denied or blocked by browser settings. Browsers remember this choice and will not ask again until you reset it."}
            </p>
          </div>
        </div>

        <div className="flex justify-end gap-2">
          <Button variant="outline" onClick={() => setHelpKind(null)}>
            Close
          </Button>
          <Button
            onClick={() => {
              setHelpKind(null);
              window.location.reload();
            }}
          >
            <ExternalLink className="mr-1.5 h-3.5 w-3.5" />
            Refresh Page
          </Button>
        </div>
      </DialogContent>
    </Dialog>
  );

  if (variant === "inline") {
    return (
      <>
        {helpDialog}
        <VoiceInputOverlay
          open={isActive}
          onStop={stopListening}
          onCancel={cancelListening}
          duration={duration}
          audioLevel={audioLevel}
          interimTranscript={interimTranscript}
          isProcessing={isProcessing}
        >
          <Tooltip>
            <TooltipTrigger asChild>
              <Button
                type="button"
                variant="ghost"
                size="icon"
                className={cn(
                  "h-7 w-7 rounded-full",
                  isListening && "mic-recording bg-destructive/10 text-destructive",
                  className
                )}
                onClick={handleClick}
                disabled={disabled || isProcessing}
                aria-label={isListening ? "Stop recording" : "Start voice input"}
                aria-pressed={isListening}
              >
                {isProcessing ? (
                  <Loader2 className="h-3.5 w-3.5 animate-spin" />
                ) : (
                  <Mic className={cn("h-3.5 w-3.5", isListening && "text-destructive")} />
                )}
              </Button>
            </TooltipTrigger>
            {!isActive && (
              <TooltipContent side="top">
                <p>Dictate with voice</p>
              </TooltipContent>
            )}
          </Tooltip>
        </VoiceInputOverlay>
      </>
    );
  }

  return (
    <>
      {helpDialog}
      <VoiceInputOverlay
        open={isActive}
        onStop={stopListening}
        onCancel={cancelListening}
        duration={duration}
        audioLevel={audioLevel}
        interimTranscript={interimTranscript}
        isProcessing={isProcessing}
      >
        <Tooltip>
          <TooltipTrigger asChild>
            <Button
              type="button"
              variant={isListening ? "destructive" : "outline"}
              size="sm"
              className={cn("h-7 gap-1.5 text-xs", isListening && "mic-recording", className)}
              onClick={handleClick}
              disabled={disabled || isProcessing}
              aria-label={isListening ? "Stop recording" : "Start voice input"}
              aria-pressed={isListening}
            >
              {isProcessing ? (
                <Loader2 className="h-3 w-3 animate-spin" />
              ) : (
                <Mic className="h-3 w-3" />
              )}
              <span className="hidden sm:inline">{isListening ? "Stop" : "Dictate"}</span>
            </Button>
          </TooltipTrigger>
          {!isActive && (
            <TooltipContent side="top">
              <p>Dictate with voice (Ctrl+Shift+M)</p>
            </TooltipContent>
          )}
        </Tooltip>
      </VoiceInputOverlay>
    </>
  );
}
